import { useState } from "react";
import { api, COMPANY } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Phone, MessageSquare, Mail, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { onCallClick, onTextClick } from "@/lib/dialer";

export default function ContactPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name:"", phone:"", email:"", message:"", kind:"contact" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k,v) => setForm(f=>({...f, [k]:v}));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, language: lang });
      setDone(true);
      toast.success("Message sent");
    } catch { toast.error("Could not send — please call us."); }
    finally { setLoading(false); }
  };

  return (
    <main className="section-pad" data-testid="contact-page">
      <div className="mx-auto max-w-6xl px-5 md:px-8 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-ice-400">Contact</div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-black">{t("contact.title")}</h1>
          <p className="mt-3 text-silver-300 max-w-md">{t("contact.sub")}</p>

          <div className="mt-8 grid gap-4">
            <a href={`tel:${COMPANY.phoneTel}`} onClick={onCallClick} className="card-ink rounded-2xl p-5 flex items-center gap-4" data-testid="contact-call">
              <div className="size-11 rounded-xl bg-ice-400/15 ring-1 ring-ice-400/30 grid place-items-center"><Phone className="size-5 text-ice-400"/></div>
              <div><div className="text-xs uppercase tracking-[0.2em] text-silver-400">Call</div><div className="font-display font-bold text-lg">{COMPANY.phone}</div></div>
            </a>
            <a href={COMPANY.sms} onClick={onTextClick} className="card-ink rounded-2xl p-5 flex items-center gap-4" data-testid="contact-text">
              <div className="size-11 rounded-xl bg-ice-400/15 ring-1 ring-ice-400/30 grid place-items-center"><MessageSquare className="size-5 text-ice-400"/></div>
              <div><div className="text-xs uppercase tracking-[0.2em] text-silver-400">Text</div><div className="font-display font-bold text-lg">{COMPANY.phone}</div></div>
            </a>
            <a href={`mailto:${COMPANY.email}`} className="card-ink rounded-2xl p-5 flex items-center gap-4" data-testid="contact-email">
              <div className="size-11 rounded-xl bg-ice-400/15 ring-1 ring-ice-400/30 grid place-items-center"><Mail className="size-5 text-ice-400"/></div>
              <div><div className="text-xs uppercase tracking-[0.2em] text-silver-400">Email</div><div className="font-display font-bold text-lg">{COMPANY.email}</div></div>
            </a>
          </div>
        </div>

        {done ? (
          <div className="rounded-3xl bg-ice-400/10 ring-1 ring-ice-400/30 p-8 text-center self-start" data-testid="contact-success">
            <CheckCircle2 className="size-10 text-ice-400 mx-auto"/>
            <h2 className="mt-3 font-display text-2xl font-black">Got it!</h2>
            <p className="mt-2 text-silver-300">We&apos;ll respond shortly. For urgent issues, please call.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl bg-ink-800 ring-1 ring-white/10 p-6 md:p-8 grid gap-4" data-testid="contact-form">
            <div className="flex gap-2">
              {[{k:"contact",l:"General"},{k:"emergency",l:"Emergency"}].map(o => (
                <button type="button" key={o.k} onClick={()=>set("kind",o.k)} className={`px-3 py-2 rounded-full text-xs ring-1 flex items-center gap-1.5 ${form.kind===o.k?"ring-ice-400 bg-ice-400/10 text-ice-400":"ring-white/10 text-silver-300"}`} data-testid={`contact-kind-${o.k}`}>
                  {o.k==="emergency" && <AlertTriangle className="size-3"/>} {o.l}
                </button>
              ))}
            </div>
            <input required placeholder="Your name" className="input" value={form.name} onChange={e=>set("name",e.target.value)} data-testid="contact-name"/>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Phone" className="input" value={form.phone} onChange={e=>set("phone",e.target.value)} data-testid="contact-phone"/>
              <input placeholder="Email" type="email" className="input" value={form.email} onChange={e=>set("email",e.target.value)} data-testid="contact-email-input"/>
            </div>
            <textarea required placeholder="How can we help?" className="input min-h-[140px]" value={form.message} onChange={e=>set("message",e.target.value)} data-testid="contact-message"/>
            <button disabled={loading} className="btn-primary rounded-full px-5 py-3 text-sm flex items-center gap-2 w-fit disabled:opacity-50" data-testid="contact-submit"><Send className="size-4"/> {loading?"Sending...":"Send Message"}</button>
            <p className="text-xs text-silver-400">Message will be translated to English for our team if you write in another language.</p>
          </form>
        )}
      </div>
      <style>{`.input{background:#1C274C;border-radius:.75rem;padding:.75rem 1rem;font-size:.875rem;outline:none;width:100%;border:1px solid rgba(255,255,255,0.08);color:white;}
      .input:focus{border-color:#00A8E8;box-shadow:0 0 0 3px rgba(0,168,232,0.18);}`}</style>
    </main>
  );
}
